import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ShieldCheck, LogOut, Loader2, Radio, ArrowLeft, RefreshCcw, Zap,
  CheckCircle2, AlertTriangle, XCircle, Activity, Package, MapPin,
} from 'lucide-react';
import { adminApi, clearAdminToken } from '../../services/adminApi';

const POLL_INTERVAL_MS = 15000;

const formatUptime = (seconds) => {
  if (!seconds && seconds !== 0) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const formatRelative = (seconds) => {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
};

const StatusPill = ({ status }) => {
  const cfg = {
    healthy:  { Icon: CheckCircle2,  cls: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30', label: 'Healthy' },
    stale:    { Icon: AlertTriangle, cls: 'text-amber-400 bg-amber-500/10 border-amber-500/30', label: 'Stale' },
    offline:  { Icon: XCircle,       cls: 'text-red-400 bg-red-500/10 border-red-500/30', label: 'Offline' },
  }[status] || { Icon: XCircle, cls: 'text-red-400 bg-red-500/10 border-red-500/30', label: status };
  const { Icon, cls, label } = cfg;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-sm border px-2 py-1 text-[10px] font-mono uppercase tracking-widest ${cls}`}
      data-testid={`gateway-status-pill-${status}`}
    >
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

const Metric = ({ label, value, sub, testid, tone = 'default' }) => (
  <div
    className={`p-4 rounded-md border ${
      tone === 'warn'
        ? 'border-amber-500/40 bg-amber-500/5'
        : tone === 'bad'
        ? 'border-red-500/40 bg-red-500/5'
        : 'border-border bg-secondary/30'
    }`}
    data-testid={testid}
  >
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="text-2xl font-mono font-semibold mt-1">{value}</div>
    {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);

const AdminTelematicsPage = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const { data } = await adminApi.telematicsStatus();
      setData(data);
    } catch (err) {
      if (err.response?.status === 401) {
        clearAdminToken();
        navigate('/admin/login', { replace: true });
      } else if (!silent) {
        toast.error('Failed to load gateway status');
      }
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        const meRes = await adminApi.me();
        if (!cancelled) setMe(meRes.data);
      } catch {
        clearAdminToken();
        navigate('/admin/login', { replace: true });
        return;
      }
      load();
    };
    init();
    const interval = setInterval(() => load(true), POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [load, navigate]);

  const handleSelfTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await adminApi.telematicsSelfTest();
      setTestResult(data);
      if (data.ok) {
        toast.success('Gateway self-test passed');
      } else {
        toast.error('Gateway self-test failed — see verdict below');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Self-test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleLogout = () => {
    clearAdminToken();
    navigate('/admin/login', { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const gw = data?.gateway || {};
  const fleet = data?.fleet || {};
  const packets = data?.recent_packets || [];

  return (
    <div className="min-h-screen bg-background text-foreground">

      {/* Header */}
      <header className="border-b border-border bg-secondary/30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5 text-sm"
              data-testid="back-to-admin"
            >
              <ArrowLeft className="w-4 h-4" /> Tenants
            </button>
            <div className="w-px h-5 bg-border" />
            <div className="flex items-center gap-2">
              <Radio className="w-5 h-5 text-primary" />
              <h1 className="font-semibold tracking-tight text-lg">Telematics Gateway</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground hidden md:inline" data-testid="admin-identity">
              <ShieldCheck className="w-3 h-3 inline mr-1" />
              {me?.email}
            </span>
            <button
              onClick={() => load()}
              disabled={refreshing}
              className="text-xs px-3 py-1.5 rounded-sm border border-border hover:bg-secondary transition-colors flex items-center gap-1.5"
              data-testid="refresh-status"
            >
              <RefreshCcw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleLogout}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
              data-testid="admin-logout"
            >
              <LogOut className="w-3 h-3" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Status hero */}
        <section className="space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h2 className="text-sm uppercase tracking-widest text-muted-foreground">Gateway status</h2>
            <StatusPill status={gw.status || 'offline'} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Metric
              label="Last heartbeat"
              value={formatRelative(gw.seconds_since_seen)}
              sub={gw.host && `${gw.host}:${gw.port}`}
              testid="gw-last-heartbeat"
              tone={gw.status === 'offline' ? 'bad' : gw.status === 'stale' ? 'warn' : 'default'}
            />
            <Metric
              label="Uptime"
              value={formatUptime(gw.uptime_seconds)}
              sub={gw.version && `v${gw.version}`}
              testid="gw-uptime"
            />
            <Metric
              label="Live connections"
              value={gw.active_connections ?? '—'}
              sub={`${gw.active_imeis_count ?? 0} IMEI(s) mid-session`}
              testid="gw-connections"
            />
            <Metric
              label="Records forwarded"
              value={(gw.records_forwarded_total ?? 0).toLocaleString()}
              sub={`${(gw.records_in_total ?? 0).toLocaleString()} parsed since boot`}
              testid="gw-records"
            />
          </div>

          {/* Diagnostic block — only when gateway is stale or offline */}
          {gw.diagnostic && (
            <div
              className={`mt-4 rounded-md border p-4 ${
                gw.diagnostic.severity === 'critical'
                  ? 'border-red-500/50 bg-red-500/5'
                  : 'border-amber-500/50 bg-amber-500/5'
              }`}
              data-testid="gateway-diagnostic"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle
                  className={`w-5 h-5 shrink-0 mt-0.5 ${
                    gw.diagnostic.severity === 'critical' ? 'text-red-400' : 'text-amber-400'
                  }`}
                />
                <div className="flex-1 space-y-3">
                  <p className="text-sm font-semibold" data-testid="gateway-diagnostic-headline">
                    {gw.diagnostic.headline}
                  </p>
                  {gw.diagnostic.likely_causes?.length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Likely causes</p>
                      <ul className="text-xs space-y-1 list-disc list-inside">
                        {gw.diagnostic.likely_causes.map((c, i) => (
                          <li key={i}>{c}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {gw.diagnostic.remediation?.length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Try this</p>
                      <ul className="text-xs space-y-1 list-disc list-inside">
                        {gw.diagnostic.remediation.map((c, i) => (
                          <li key={i}>
                            {c.includes('`') ? (
                              <span dangerouslySetInnerHTML={{
                                __html: c.replace(
                                  /`([^`]+)`/g,
                                  '<code class="font-mono text-[#FFD108] bg-black/40 px-1 py-0.5 rounded-sm">$1</code>'
                                )
                              }} />
                            ) : (
                              c
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Self-test */}
        <section className="rounded-md border border-border bg-secondary/20 p-6 space-y-4">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-semibold text-base flex items-center gap-2">
                <Zap className="w-4 h-4 text-primary" />
                Self-test
              </h3>
              <p className="text-xs text-muted-foreground mt-1 max-w-xl">
                Fires a synthetic Teltonika IMEI handshake at <span className="font-mono">{gw.host || 'gps.equiprsoftware.com'}:{gw.port || 5027}</span>{' '}
                from Guardian's backend. Confirms TCP reachability, gateway service health, and that the gateway can authenticate
                against this Guardian instance — all in one round-trip.
              </p>
            </div>
            <button
              onClick={handleSelfTest}
              disabled={testing}
              className="px-4 py-2 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
              data-testid="run-self-test"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
              {testing ? 'Testing…' : 'Run self-test'}
            </button>
          </div>
          {testResult && (
            <div
              className={`rounded-sm border p-4 ${
                testResult.ok
                  ? 'border-emerald-500/40 bg-emerald-500/5'
                  : 'border-red-500/40 bg-red-500/5'
              }`}
              data-testid="self-test-result"
            >
              <div className="flex items-center gap-2 mb-2">
                {testResult.ok ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-400" />
                )}
                <span className={`text-xs uppercase tracking-widest font-mono ${testResult.ok ? 'text-emerald-400' : 'text-red-400'}`}>
                  {testResult.ok ? 'PASS' : 'FAIL'}
                </span>
                {testResult.tcp_connect_ms != null && (
                  <span className="text-xs text-muted-foreground font-mono">· connect {testResult.tcp_connect_ms} ms</span>
                )}
                {testResult.bytes_received && (
                  <span className="text-xs text-muted-foreground font-mono">· reply 0x{testResult.bytes_received}</span>
                )}
              </div>
              <p className="text-sm leading-relaxed">{testResult.verdict}</p>
              {testResult.error && (
                <p className="text-xs text-muted-foreground mt-2 font-mono">Error: {testResult.error}</p>
              )}
            </div>
          )}
        </section>

        {/* Fleet metrics */}
        <section className="space-y-3">
          <h2 className="text-sm uppercase tracking-widest text-muted-foreground">Fleet across all tenants</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Metric
              label="Registered devices"
              value={fleet.registered_devices ?? 0}
              sub="IMEIs bound to an asset"
              testid="fleet-registered"
            />
            <Metric
              label="Reporting now"
              value={fleet.reporting_now ?? 0}
              sub="In active TCP session"
              testid="fleet-reporting"
            />
            <Metric
              label="Silent > 24h"
              value={fleet.silent_24h ?? 0}
              sub="May indicate hardware issue"
              testid="fleet-silent"
              tone={(fleet.silent_24h || 0) > 0 ? 'warn' : 'default'}
            />
            <Metric
              label="Never reported"
              value={fleet.never_reported ?? 0}
              sub="Registered but no data yet"
              testid="fleet-never"
              tone={(fleet.never_reported || 0) > 0 ? 'warn' : 'default'}
            />
          </div>
        </section>

        {/* Recent packets */}
        <section className="space-y-3">
          <h2 className="text-sm uppercase tracking-widest text-muted-foreground">Latest 10 packets · across all tenants</h2>
          <div className="rounded-md border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40 border-b border-border">
                <tr className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  <th className="text-left px-3 py-2">When</th>
                  <th className="text-left px-3 py-2">Tenant</th>
                  <th className="text-left px-3 py-2">Asset</th>
                  <th className="text-left px-3 py-2">IMEI</th>
                  <th className="text-right px-3 py-2">Speed</th>
                  <th className="text-left px-3 py-2">Position</th>
                </tr>
              </thead>
              <tbody>
                {packets.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-12 text-center text-muted-foreground text-sm">
                      <Package className="w-6 h-6 mx-auto mb-2 opacity-30" />
                      No telemetry packets received yet.
                    </td>
                  </tr>
                ) : (
                  packets.map((p, i) => (
                    <tr key={i} className="border-t border-border hover:bg-secondary/20" data-testid={`packet-row-${i}`}>
                      <td className="px-3 py-2 font-mono text-xs">
                        {p.timestamp ? new Date(p.timestamp).toLocaleString() : '—'}
                      </td>
                      <td className="px-3 py-2 text-xs">{p.tenant_name}</td>
                      <td className="px-3 py-2 text-xs">{p.asset_name}</td>
                      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                        {p.imei ? `${p.imei.slice(0, 5)}…${p.imei.slice(-5)}` : '—'}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs">
                        {p.speed_kmh != null ? `${p.speed_kmh} km/h` : '—'}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                        {p.latitude != null && p.longitude != null ? (
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {p.latitude.toFixed(4)}, {p.longitude.toFixed(4)}
                          </span>
                        ) : '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
};

export default AdminTelematicsPage;
